import React from "react";
import {
  Alert,
  AlertIcon,
  Badge,
  Box,
  Button,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Text,
  VStack,
  useColorModeValue,
  useDisclosure,
} from "@chakra-ui/react";
import { ChevronDownIcon } from "@chakra-ui/icons";
import { FiX } from "react-icons/fi";
import { CustomIcon } from "../components/CustomIcon";
import { FundSelectionCard } from "../components/funds/FundSelectionCard";
import {
  PersianDatePicker,
  type PersianDayValue,
} from "../components/common/PersianDatePicker";
import { CrudPage } from "../components/CrudPage";
import { RecordDetailsModal } from "../components/RecordDetailsModal";
import { TadbirFund, useFunds } from "../hooks/useFunds";
import { toGregorian } from "jalaali-js";
import { tadbir } from "../services/api";
import { useAuth } from "../context/AuthContext";
import {
  getUserIdentifier,
  getUserIdentifierLabel,
} from "../utils/userIdentity";

const pageSize = 20;

const getNestedValue = (item: any, path: string) =>
  path.split(".").reduce((current, key) => current?.[key], item);

const getValue = (item: any, keys: string[]) => {
  for (const key of keys) {
    const value = key.includes(".") ? getNestedValue(item, key) : item?.[key];
    if (value !== undefined && value !== null && value !== "") {
      return value;
    }
  }
  return "—";
};

const formatMaybeNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return "—";

  const numericValue = Number(value);
  return Number.isFinite(numericValue)
    ? numericValue.toLocaleString("fa-IR")
    : String(value);
};

const formatMaybeDate = (value: unknown) => {
  if (!value) return "—";

  const parsedValue = new Date(String(value));
  return Number.isNaN(parsedValue.getTime())
    ? String(value)
    : parsedValue.toLocaleDateString("fa-IR");
};

const getStateBadgeColor = (stateKey: unknown) => {
  const normalized = String(stateKey ?? "").toLowerCase();

  if (
    ["done", "success", "confirmed", "approved", "completed"].includes(
      normalized,
    )
  ) {
    return "green";
  }

  if (["pending", "waiting", "processing", "inprogress"].includes(normalized)) {
    return "yellow";
  }

  if (
    ["rejected", "failed", "canceled", "cancelled", "error"].includes(
      normalized,
    )
  ) {
    return "red";
  }

  return "gray";
};

const columns = [
  {
    header: "کد ملی",
    accessor: "nationalCode",
    render: (item: any) => getValue(item, ["nationalCode", "NationalCode"]),
  },
  {
    header: "نوع درخواست",
    accessor: "typeName",
    render: (item: any) =>
      getValue(item, ["typeName", "TypeName", "type.name", "Type.Name"]),
  },
  {
    header: "وضعیت",
    accessor: "stateName",
    render: (item: any) => {
      const stateName = getValue(item, ["stateName", "StateName"]);
      const stateKey = getValue(item, ["stateKey", "StateKey"]);

      return (
        <Badge
          colorScheme={getStateBadgeColor(stateKey)}
          variant="subtle"
          px={3}
          py={1}
          borderRadius="full"
          fontSize="xs"
        >
          {String(stateName)}
        </Badge>
      );
    },
  },
  {
    header: "تعداد",
    accessor: "count",
    render: (item: any) =>
      formatMaybeNumber(getValue(item, ["count", "Count"])),
  },
  {
    header: "تاریخ ثبت",
    accessor: "requestDate",
    render: (item: any) =>
      formatMaybeDate(getValue(item, ["requestDate", "RequestDate"])),
  },
  {
    header: "مبلغ",
    accessor: "amount",
    render: (item: any) =>
      formatMaybeNumber(getValue(item, ["amount", "Amount"])),
  },
  {
    header: "تاریخ تایید",
    accessor: "confirmDate",
    render: (item: any) =>
      formatMaybeDate(getValue(item, ["confirmDate", "ConfirmDate"])),
  },
  {
    header: "NAV",
    accessor: "navValue",
    render: (item: any) =>
      formatMaybeNumber(getValue(item, ["navValue", "NavValue"])),
  },
  {
    header: "صندوق",
    accessor: "fundTitle",
    render: (item: any) =>
      getValue(item, ["fund.title", "Fund.Title", "fundTitle", "FundTitle"]),
  },
];

const toGregorianDate = (value: PersianDayValue) => {
  if (!value) return "";

  const gregorianDate = toGregorian(value.year, value.month, value.day);

  return [
    gregorianDate.gy,
    String(gregorianDate.gm).padStart(2, "0"),
    String(gregorianDate.gd).padStart(2, "0"),
  ].join("-");
};

const formatCompactPersianDate = (value: PersianDayValue) => {
  if (!value) return "—";

  return [value.year, value.month, value.day].join("/");
};

const toGregorianNativeDate = (value: PersianDayValue) => {
  if (!value) return null;

  const gregorianDate = toGregorian(value.year, value.month, value.day);
  return new Date(gregorianDate.gy, gregorianDate.gm - 1, gregorianDate.gd);
};

const getTodayPersianDay = (): NonNullable<PersianDayValue> => {
  const formatter = new Intl.DateTimeFormat("en-US-u-ca-persian-nu-latn", {
    year: "numeric",
    month: "numeric",
    day: "numeric",
  });
  const parts = formatter.formatToParts(new Date());
  const year = Number(parts.find((part) => part.type === "year")?.value);
  const month = Number(parts.find((part) => part.type === "month")?.value);
  const day = Number(parts.find((part) => part.type === "day")?.value);

  return { year, month, day };
};

const findFundBySeo = (funds: TadbirFund[], selectedFund: string) =>
  funds.find(
    (fund) =>
      String(fund.nav?.seoRegisterNo || fund.fundRegisterCode) === selectedFund,
  );

export const ExternalRequestsPage: React.FC = () => {
  const { user } = useAuth();
  const identifier = getUserIdentifier(user);
  const identifierLabel = getUserIdentifierLabel(user);
  const { data: fundsData, isLoading: isFundsLoading } = useFunds();
  const funds = React.useMemo(() => fundsData?.items || [], [fundsData]);
  const filterModal = useDisclosure();
  const cardBg = useColorModeValue("white", "dark.50");
  const mutedColor = useColorModeValue("gray.500", "gray.400");
  const todayPersianDate = React.useMemo(() => getTodayPersianDay(), []);
  const [selectedFund, setSelectedFund] = React.useState("");
  const [fromDate, setFromDate] = React.useState<PersianDayValue>(null);
  const [toDate, setToDate] = React.useState<PersianDayValue>(todayPersianDate);
  const [draftFromDate, setDraftFromDate] =
    React.useState<PersianDayValue>(null);
  const [draftToDate, setDraftToDate] =
    React.useState<PersianDayValue>(todayPersianDate);
  const [selectedItem, setSelectedItem] = React.useState<any>(null);

  const appliedFromDate = React.useMemo(
    () => (fromDate ? toGregorianDate(fromDate) : undefined),
    [fromDate],
  );
  const appliedToDate = React.useMemo(
    () => (toDate ? toGregorianDate(toDate) : undefined),
    [toDate],
  );

  React.useEffect(() => {
    if (filterModal.isOpen) {
      setDraftFromDate(fromDate);
      setDraftToDate(toDate || todayPersianDate);
    }
  }, [filterModal.isOpen, fromDate, toDate, todayPersianDate]);

  React.useEffect(() => {
    if (draftFromDate && draftToDate) {
      const fromValue = toGregorianNativeDate(draftFromDate);
      const toValue = toGregorianNativeDate(draftToDate);
      if (fromValue && toValue && fromValue > toValue) {
        setDraftToDate(draftFromDate);
      }
    }
  }, [draftFromDate, draftToDate]);

  React.useEffect(() => {
    setToDate(todayPersianDate);
    setDraftToDate(todayPersianDate);
  }, [todayPersianDate]);

  React.useEffect(() => {
    if (!selectedFund && funds.length > 0) {
      const firstFund = funds[0];
      setSelectedFund(
        String(firstFund.nav?.seoRegisterNo || firstFund.fundRegisterCode),
      );
    }
  }, [funds, selectedFund]);

  if (!identifier) {
    return (
      <Alert status="warning" borderRadius="lg">
        <AlertIcon />
        {identifierLabel} کاربر برای دریافت کلیه درخواست‌ها یافت نشد.
      </Alert>
    );
  }

  if (isFundsLoading) {
    return (
      <VStack
        w="100%"
        h="100dvh"
        py={10}
        spacing={4}
        align="center"
        justify="center"
      >
        <Spinner size="xl" color="brand.500" />
        <Text color={mutedColor}>در حال دریافت صندوق‌ها...</Text>
      </VStack>
    );
  }

  const applyDateFilters = () => {
    setFromDate(draftFromDate);
    setToDate(draftToDate || todayPersianDate);
    filterModal.onClose();
  };

  const clearDateFilters = () => {
    setDraftFromDate(null);
    setDraftToDate(todayPersianDate);
    setFromDate(null);
    setToDate(todayPersianDate);
    filterModal.onClose();
  };

  const onClickClearFilters = (e: React.MouseEvent<HTMLElement>) => {
    if (!fromDate) {
      filterModal.onOpen();
    } else {
      e.preventDefault();
      e.stopPropagation();
      clearDateFilters();
    }
  };

  const selectedFundData = findFundBySeo(funds, selectedFund);
  const isDateFiltered = !!fromDate;
  const canApplyDateFilters = !!draftToDate;

  const quickAccess = (
    <VStack align="stretch" spacing={5}>
      <Box borderRadius="3xl" bg={cardBg}>
        <VStack align="stretch" spacing={5}>
          <HStack
            justify="space-between"
            align={{ base: "start", md: "center" }}
            flexWrap="wrap"
            spacing={3}
          >
            <VStack align="start" spacing={1}>
              <Heading size="md">دسترسی سریع صندوق‌ها</Heading>
              <Text color={mutedColor} fontSize="sm">
                کلیه درخواست‌ها صندوق فعال را ببینید و در صورت نیاز بازه تاریخ
                را محدود کنید
              </Text>
            </VStack>

            <HStack spacing={3} flexWrap="wrap">
              <HStack spacing={0} dir="rtl">
                <Button
                  as="button"
                  type="button"
                  onClick={filterModal.onOpen}
                  size="md"
                  leftIcon={
                    isDateFiltered ? (
                      <Box
                        as="span"
                        display="inline-flex"
                        alignItems="center"
                        justifyContent="center"
                        cursor="pointer"
                        borderRadius="full"
                        p={1}
                        bg="red.500"
                        _hover={{
                          bg: "red.500",
                          color: "white",
                        }}
                        transition="all .2s"
                        onClick={onClickClearFilters}
                      >
                        <CustomIcon icon={FiX} size={14} />
                      </Box>
                    ) : undefined
                  }
                  rightIcon={<ChevronDownIcon boxSize={5} />}
                  borderRadius="full"
                  fontSize="sm"
                  fontWeight="semibold"
                  colorScheme={isDateFiltered ? "green" : "gray"}
                  variant={isDateFiltered ? "solid" : "outline"}
                  borderWidth="1px"
                >
                  {isDateFiltered
                    ? `از ${formatCompactPersianDate(fromDate)} تا ${formatCompactPersianDate(toDate)}`
                    : `تا تاریخ ${formatCompactPersianDate(toDate)}`}
                </Button>
              </HStack>
            </HStack>
          </HStack>

          <FundSelectionCard
            fundData={funds}
            value={selectedFund}
            onChange={setSelectedFund}
          />
        </VStack>
      </Box>
    </VStack>
  );

  return (
    <VStack align="stretch" spacing={5} w="100%" h="100%">
      <CrudPage
        title={
          selectedFundData
            ? `کلیه درخواست‌ها ${selectedFundData.fundName.trim()}`
            : "کلیه درخواست‌ها"
        }
        columns={columns}
        queryKey="externalRequests"
        resourceName="externalRequests"
        api={{
          getAll: (page, size) =>
            tadbir.getExternalRequests({
              pageNumber: page,
              pageSize: size,
              fromDate: appliedFromDate,
              toDate: appliedToDate,
              fundSeoRegisterNoList: selectedFund,
            }),
        }}
        FormComponent={() => null}
        showAddButton={false}
        enableEdit={false}
        enableDelete={false}
        enableView={false}
        pageSize={pageSize}
        queryDependencies={[selectedFund, appliedFromDate, appliedToDate]}
        queryEnabled={!!identifier && !!selectedFund}
        keepPreviousData={false}
        beforeTable={quickAccess}
        afterTable={
          <>
            <Modal
              isOpen={filterModal.isOpen}
              onClose={filterModal.onClose}
              size="sm"
            >
              <ModalOverlay />
              <ModalContent pb={3} overflow="visible">
                <ModalHeader>فیلتر تاریخ کلیه درخواست‌ها</ModalHeader>
                <ModalCloseButton left={4} right="auto" aria-label="بستن" />
                <ModalBody py={4} overflow="visible">
                  <VStack spacing={4}>
                    <FormControl>
                      <FormLabel>از تاریخ</FormLabel>
                      <PersianDatePicker
                        value={draftFromDate}
                        onChange={setDraftFromDate}
                        placeholder="تاریخ شروع"
                      />
                    </FormControl>
                    <FormControl>
                      <FormLabel>تا تاریخ</FormLabel>
                      <PersianDatePicker
                        value={draftToDate}
                        onChange={setDraftToDate}
                        placeholder="امروز"
                        maximumDate={todayPersianDate}
                      />
                    </FormControl>
                  </VStack>
                </ModalBody>
                <ModalFooter>
                  <VStack spacing={3} w="100%">
                    <Button
                      colorScheme="brand"
                      onClick={applyDateFilters}
                      isDisabled={!canApplyDateFilters}
                      w="100%"
                      borderRadius="xl"
                    >
                      اعمال
                    </Button>
                    <Button
                      variant="outline"
                      onClick={clearDateFilters}
                      w="100%"
                      borderRadius="xl"
                    >
                      حذف فیلتر
                    </Button>
                  </VStack>
                </ModalFooter>
              </ModalContent>
            </Modal>

            <RecordDetailsModal
              isOpen={!!selectedItem}
              onClose={() => setSelectedItem(null)}
              title="جزئیات درخواست خارجی"
              data={selectedItem}
            />
          </>
        }
      />
    </VStack>
  );
};
